import { useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  qInventoryAdjustmentsList,
  qInventoryAdjustmentLinesByAdjustment,
  type InventoryAdjustmentRow,
  type InventoryAdjustmentLineRow,
} from '@helios/data-access';
import { usePowerSyncQuery } from '../../adapters/powersync';
import { type ShellOutletContext } from '../../shell/shared';
import {
  InventoryListToolbar,
  InventoryPageShell,
  InventoryPrimaryAction,
  InventoryRow,
  InventoryRowActions,
  InventoryStatusBadge,
  InventoryTable,
  type InventoryStatusVariant,
} from './_shared';

const COLUMNS = [
  { label: 'Adjustment No.', width: '180px' },
  { label: 'Date', width: '120px' },
  { label: 'Location', width: '160px' },
  { label: 'Lines', width: '90px', align: 'right' as const },
  { label: 'Remarks', width: '1.5fr' },
  { label: 'Status', width: '110px' },
  { label: 'Actions', width: '90px', align: 'right' as const },
];

function statusToVariant(status: string | null): InventoryStatusVariant {
  if (status === 'cancelled') return 'cancelled';
  if (status === 'draft') return 'draft';
  return 'completed';
}

export function InventoryAdjustmentsPage() {
  const { collapsed = false, onToggleSidebar } = useOutletContext<ShellOutletContext>() ?? {
    collapsed: false,
  };
  const navigate = useNavigate();

  const { data: rows = [] } = usePowerSyncQuery<InventoryAdjustmentRow>(
    qInventoryAdjustmentsList(),
  );

  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) => {
      return (
        (r.adjustment_number ?? '').toLowerCase().includes(q) ||
        (r.remarks ?? '').toLowerCase().includes(q) ||
        (r.location ?? '').toLowerCase().includes(q)
      );
    });
  }, [rows, search]);

  return (
    <InventoryPageShell
      title="Inventory Adjustments"
      subtitle="Manual stock changes — additions, deductions, and corrections"
      collapsed={collapsed}
      onToggleSidebar={onToggleSidebar}
      primaryAction={
        <InventoryPrimaryAction
          label="New Adjustment"
          onClick={() => navigate('/inventory/adjustments/new')}
        />
      }
    >
      <InventoryListToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search adjustment number, remarks, location..."
      />

      <div style={{ height: 'var(--sp-4)', flexShrink: 0 }} />

      <InventoryTable
        columns={COLUMNS}
        empty={
          filtered.length === 0
            ? {
                show: true,
                title: 'No stock adjustments yet.',
                hint: 'Record an adjustment to correct stock counts or log damaged/expired items.',
              }
            : undefined
        }
      >
        {filtered.map((row) => (
          <AdjustmentRow key={row.id} row={row} />
        ))}
      </InventoryTable>
    </InventoryPageShell>
  );
}

function AdjustmentRow({ row }: { row: InventoryAdjustmentRow }) {
  const { data: lineRows = [] } = usePowerSyncQuery<InventoryAdjustmentLineRow>(
    qInventoryAdjustmentLinesByAdjustment(row.id),
  );
  return (
    <InventoryRow columns={COLUMNS}>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-semi)',
          color: 'var(--text)',
        }}
      >
        {row.adjustment_number ?? row.id.slice(0, 8)}
      </span>
      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
        {row.adjustment_date ?? '—'}
      </span>
      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text)' }}>
        {row.location ?? '—'}
      </span>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text)',
          textAlign: 'right',
        }}
      >
        {lineRows.length}
      </span>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text-muted)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {row.remarks ?? '—'}
      </span>
      <InventoryStatusBadge status={statusToVariant(row.status)} />
      <InventoryRowActions />
    </InventoryRow>
  );
}
